import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Observable } from 'rxjs';

// ─── Model Interfaces ────────────────────────────────────────────────────────

export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  roles: string[];
  is_active: boolean;
  profile_photo_url?: string;
  created_at?: string;
  updated_at?: string;
  /** When true, user must change password before using the app (e.g. after invite) */
  force_password_reset?: boolean;
}

export interface UpdateMePayload {
  first_name?: string;
  last_name?: string;
  email?: string;
}

export interface ChangePasswordPayload {
  current_password: string;
  new_password: string;
}

export type UserRole = 'super_admin' | 'admin' | 'content_creator';

export interface InviteUserPayload {
  email: string;
  first_name: string;
  last_name: string;
  roles?: UserRole[];
}

export interface InviteUserResponse {
  user: User;
  message: string;
}

/** Query params for GET /api/v1/users/ (paginated) */
export interface GetUsersParams {
  page?: number;
  page_size?: number;
  search?: string;
  role?: UserRole;
  is_active?: boolean;
  force_password_reset?: boolean;
  sort_by?: 'created_at' | 'first_name' | 'last_name' | 'email';
  sort_order?: 'asc' | 'desc';
}

/** Response shape for GET /api/v1/users/ */
export interface UsersListResponse {
  items: User[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
}

// ─── Service ─────────────────────────────────────────────────────────────────

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private http = inject(HttpClient);
  private apiUrl = `${environment.apiUrl}/users`;

  /** GET /api/v1/users/me */
  getMe(): Observable<User> {
    return this.http.get<User>(`${this.apiUrl}/me`);
  }

  /** PUT /api/v1/users/me */
  updateMe(payload: UpdateMePayload): Observable<User> {
    return this.http.put<User>(`${this.apiUrl}/me`, payload);
  }

  /** POST /api/v1/users/me/photo */
  uploadPhoto(file: File): Observable<User> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<User>(`${this.apiUrl}/me/photo`, formData);
  }

  /** POST /api/v1/users/me/change-password */
  changePassword(payload: ChangePasswordPayload): Observable<any> {
    return this.http.post(`${this.apiUrl}/me/change-password`, payload);
  }

  /** GET /api/v1/users/ (paginated, with search & filters) */
  getUsers(params?: GetUsersParams): Observable<UsersListResponse> {
    let url = `${this.apiUrl}/`;
    if (params && Object.keys(params).length > 0) {
      const q = new URLSearchParams();
      if (params.page != null) q.set('page', String(params.page));
      if (params.page_size != null) q.set('page_size', String(params.page_size));
      if (params.search != null && params.search.trim() !== '') q.set('search', params.search.trim());
      if (params.role != null) q.set('role', params.role);
      if (params.is_active != null) q.set('is_active', String(params.is_active));
      if (params.force_password_reset != null) q.set('force_password_reset', String(params.force_password_reset));
      if (params.sort_by != null) q.set('sort_by', params.sort_by);
      if (params.sort_order != null) q.set('sort_order', params.sort_order);
      const query = q.toString();
      if (query) url += '?' + query;
    }
    return this.http.get<UsersListResponse>(url);
  }

  /** GET /api/v1/users/{user_id} */
  getUserById(id: string): Observable<User> {
    return this.http.get<User>(`${this.apiUrl}/${id}`);
  }

  /** PUT /api/v1/users/{user_id}/roles */
  updateUserRoles(id: string, roles: string[]): Observable<User> {
    return this.http.put<User>(`${this.apiUrl}/${id}/roles`, { roles });
  }

  /** PATCH /api/v1/users/{user_id}/deactivate */
  deactivateUser(id: string): Observable<User> {
    return this.http.patch<User>(`${this.apiUrl}/${id}/deactivate`, {});
  }

  /** PATCH /api/v1/users/{user_id}/activate */
  activateUser(id: string): Observable<User> {
    return this.http.patch<User>(`${this.apiUrl}/${id}/activate`, {});
  }

  /** POST /api/v1/users/invite */
  inviteUser(payload: InviteUserPayload): Observable<InviteUserResponse> {
    return this.http.post<InviteUserResponse>(`${this.apiUrl}/invite`, payload);
  }
}
